import { AlertTriangle, Info } from 'lucide-react';

/**
 * ETAPA VISUAL 4 — FORMULÁRIOS
 *
 * O diagnóstico mediu quatro problemas nas telas de formulário:
 *
 *   1. AGRUPAMENTO FRACO — as seções eram um `<h3>` solto seguido, 15 linhas
 *      depois, de um `<Separator>` decorativo (4 deles, todos em Regras
 *      Federativo). Nada dizia "estes campos são um assunto".
 *   2. TEXTO AUXILIAR ILEGÍVEL — 27 dicas em `text-micro`, cada uma escrita
 *      à mão com o seu próprio cinza.
 *   3. AÇÃO EM LUGARES DIFERENTES — a barra de salvar tinha 4 dialetos de
 *      classes diferentes (largura, fundo, espaçamento, margem negativa).
 *   4. CAMPOS SEM ASSOCIAÇÃO — 65 de 148 rótulos sem `htmlFor`/`id`.
 *
 * Estes três componentes resolvem 1, 2 e 3. O item 4 é feito campo a campo
 * nas telas (não dá para automatizar sem trocar os `<Label>` existentes).
 *
 * REGRA: nada aqui valida, transforma ou envia dado. É só apresentação.
 */

/**
 * Cabeçalho de seção: medalhão + título + linha fina.
 *
 * Existe separado do `SecaoFormulario` porque há formulários que são UMA grade
 * (`grid grid-cols-2`) com 20+ campos e sem nenhum agrupamento — o de
 * Campeonato, por exemplo. Nesses, envolver em seções exigiria QUEBRAR a grade
 * e reposicionar todo campo (risco alto no caminho crítico). Com o cabeçalho
 * solto, ele entra como um item de largura total (`className="md:col-span-2"`)
 * e agrupa visualmente **sem mover um único campo**.
 */
export function CabecalhoSecao({ titulo, icone: Icone, acoes, className = '' }) {
  return (
    <div className={`space-y-3 ${className}`} data-testid="cabecalho-secao">
      <div className="flex items-center gap-2">
        {Icone && (
          <span className="medalhao medalhao--suave medalhao--sm" aria-hidden="true">
            <Icone size={16} />
          </span>
        )}
        <h3 className="rotulo-secao">{titulo}</h3>
        {acoes && <div className="ml-auto flex items-center gap-2">{acoes}</div>}
      </div>

      <div className="border-t border-[#27272A]" aria-hidden="true" />
    </div>
  );
}

/**
 * Seção de formulário: cabeçalho + dica + campos.
 *
 * Substitui o par `<h3>` + `<Separator>`. O divisor é uma BORDA (`border-t`),
 * não um `bg-[#27272A]`: o tema claro sobrescreve `border-[#27272A]`, mas NÃO
 * sobrescreve `bg-[#27272A]` — uma linha pintada com ele continuaria escura
 * sobre o fundo branco.
 */
export function SecaoFormulario({
  titulo,
  icone: Icone,
  dica,
  acoes,
  children,
  className = '',
  testid = 'secao-formulario',
}) {
  return (
    <section className={`space-y-4 ${className}`} data-testid={testid}>
      <CabecalhoSecao titulo={titulo} icone={Icone} acoes={acoes} />

      {dica && <Dica>{dica}</Dica>}

      <div className="space-y-4">{children}</div>
    </section>
  );
}

/**
 * Dica de campo/seção — substitui os 27 `text-micro` escritos à mão.
 *
 * A cor sai do token `--text-secondary`, que já respeita o ajuste de contraste
 * feito na Etapa 1 (o `#71717A` antigo dava 4,09:1 no escuro, abaixo do mínimo
 * de 4,5:1). `tom="aviso"` é para o que o usuário precisa LER antes de decidir
 * (ex.: "WO não conta como luta") e tem cor própria em cada tema.
 *
 * `icone={false}` existe para nota curta de uma linha, onde o ícone só ocupa
 * espaço.
 */
export function Dica({ children, tom = 'neutro', icone = true, className = '', testid }) {
  const Icone = tom === 'aviso' ? AlertTriangle : Info;
  return (
    <p
      className={`dica ${tom === 'aviso' ? 'dica--aviso' : ''} flex items-start gap-1.5 ${className}`}
      data-testid={testid || `dica-${tom}`}
    >
      {icone && <Icone size={12} aria-hidden="true" className="mt-[2px] shrink-0" />}
      <span className="min-w-0">{children}</span>
    </p>
  );
}

/**
 * Barra de ações do formulário — UM dialeto para os quatro que existiam.
 *
 *   contexto="cartao"  → dentro de um `<Card>` com `p-6`: as margens negativas
 *                        fazem a barra encostar nas bordas do cartão.
 *   contexto="pagina"  → formulário solto na página (fundo da página).
 *   contexto="dialogo" → rodapé de `DialogContent` com `max-h-[90vh]`: precisa
 *                        de `shrink-0` para não ser esmagado pelo scroll.
 *
 * A ordem (secundária antes da primária) é convenção de leitura: a ação
 * destrutiva/neutra fica longe do canto onde o olho termina.
 */
export function BarraAcoes({ children, contexto = 'cartao', className = '' }) {
  const base =
    {
      cartao: 'sticky bottom-0 z-10 -mx-6 -mb-6 border-t border-[#27272A] bg-[#141414] px-6 py-3',
      pagina: 'sticky bottom-0 z-10 border-t border-[#27272A] bg-[#0A0A0A] py-3',
      dialogo: 'shrink-0 border-t border-[#27272A] bg-[#141414] px-6 py-3',
    }[contexto] || '';

  return (
    <div
      className={`${base} flex flex-wrap items-center justify-end gap-2 ${className}`}
      data-testid="barra-acoes"
      data-contexto={contexto}
    >
      {children}
    </div>
  );
}
